import { Group, Stack, useMantineTheme } from '@mantine/core';
import type {
	BarChartConfig,
	BiMetricConfig,
	PieChartConfig,
	ScatterChartConfig,
} from '@repo/api-codegen';
import { Switch, TextInput } from '@repo/foundations';
import { cloneDeep, keys } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { SelectableProperty } from '../../../components/EntityPageLayout/EntityPropertySidebar';
import { PropertySelector } from '../../../components/Properties/PropertySelector';
import { ChartConfigHelper } from '../../../utils/charts/bi/ChartConfigHelper';
import { toTitleCase } from '../../../utils/charts/utils';
import { getAxesSettings } from './ChartTypeSelector.utils';

interface YAxisSelectorProps {
	chartConfig: BiMetricConfig['chart_config'];
	onUpdate: (config: BiMetricConfig['chart_config']) => void;
}

const updateYAxis = (
	config: BiMetricConfig['chart_config'],
	value: string | string[],
	multipleYAxis: boolean
): BiMetricConfig['chart_config'] => {
	const newConfig = cloneDeep(config);
	if (config.chartType === 'bar' || config.chartType === 'line') {
		const barAndLineConfig = newConfig as BarChartConfig;
		barAndLineConfig.axes.y = multipleYAxis
			? (value as string[])
			: [value as string];
	}

	if (config.chartType === 'scatter') {
		const scatterConfig = newConfig as ScatterChartConfig;
		scatterConfig.axes.y = [value as string];
	}

	if (config.chartType === 'pie') {
		const pieConfig = newConfig as PieChartConfig;
		pieConfig.axes.y = multipleYAxis ? (value as string[]) : [value as string];
	}

	return newConfig;
};

const updateYAxisShowTitle = (
	config: BiMetricConfig['chart_config'],
	value: boolean
): BiMetricConfig['chart_config'] => {
	const newConfig = cloneDeep(config);
	if (!newConfig.yAxisConfig) {
		newConfig.yAxisConfig = {
			yAxisScaleType: 'linear',
		};
	}
	newConfig.yAxisConfig.yAxisShowTitle = value;
	return newConfig;
};

const updateYAxisTitle = (
	config: BiMetricConfig['chart_config'],
	value: string
): BiMetricConfig['chart_config'] => {
	const newConfig = cloneDeep(config);
	if (!newConfig.yAxisConfig) {
		newConfig.yAxisConfig = {
			yAxisScaleType: 'linear',
		};
	}
	newConfig.yAxisConfig.yAxisCustomTitle = value || null;
	return newConfig;
};

const updateYAxisShowLabel = (
	config: BiMetricConfig['chart_config'],
	value: boolean
): BiMetricConfig['chart_config'] => {
	const newConfig = cloneDeep(config);
	if (!newConfig.yAxisConfig) {
		newConfig.yAxisConfig = {
			yAxisScaleType: 'linear',
		};
	}
	newConfig.yAxisConfig.yAxisShowTicks = value;
	return newConfig;
};

const updateYAxisStartAtZero = (
	config: BiMetricConfig['chart_config'],
	value: boolean
): BiMetricConfig['chart_config'] => {
	const newConfig = cloneDeep(config);
	if (!newConfig.yAxisConfig) {
		newConfig.yAxisConfig = {
			yAxisScaleType: 'linear',
		};
	}
	newConfig.yAxisConfig.yAxisStartAtZero = value;
	return newConfig;
};

const updateYAxisScaleType = (
	config: BiMetricConfig['chart_config'],
	value: 'log' | 'linear'
): BiMetricConfig['chart_config'] => {
	const newConfig = cloneDeep(config);
	if (!newConfig.yAxisConfig) {
		newConfig.yAxisConfig = {
			yAxisScaleType: 'linear',
		};
	}
	newConfig.yAxisConfig.yAxisScaleType = value;
	return newConfig;
};

export const YAxisSection = observer(
	({ chartConfig, onUpdate }: YAxisSelectorProps) => {
		const mantineTheme = useMantineTheme();
		const multipleYAxis =
			chartConfig.chartType === 'bar' ||
			chartConfig.chartType === 'line' ||
			chartConfig.chartType === 'pie';

		const axesSettings = getAxesSettings(chartConfig);
		const yAxisColumns = axesSettings?.y || [];
		const columns = keys(chartConfig.columnConfigs);
		const chartConfigHelper = new ChartConfigHelper(chartConfig, mantineTheme);

		return (
			<Stack spacing="xs">
				<SelectableProperty label="Columns" dataTestId="y-columns">
					<Group w="100%" position="right">
						<PropertySelector
							selected={multipleYAxis ? yAxisColumns : yAxisColumns[0]}
							type={multipleYAxis ? 'multi' : 'single'}
							onChange={(value) => {
								onUpdate(
									updateYAxis(
										chartConfig,
										value as string | string[],
										multipleYAxis
									)
								);
							}}
							value="columns"
							iconType="none"
							options={columns.map((column) => ({
								label: toTitleCase(column),
								value: column,
								icon: 'column',
							}))}
							isViewerUser={false}
						/>
					</Group>
				</SelectableProperty>
				<SelectableProperty
					label="Show axis title"
					dataTestId="y-show-axis-title"
				>
					<Group w="100%" position="right">
						<Switch
							checked={chartConfigHelper.shouldShowYAxisTitle}
							onChange={(event) => {
								onUpdate(
									updateYAxisShowTitle(chartConfig, event.currentTarget.checked)
								);
							}}
						/>
					</Group>
				</SelectableProperty>

				{chartConfigHelper.shouldShowYAxisTitle && (
					<SelectableProperty label="Axis title" dataTestId="y-axis-title">
						<Group w="100%" position="right">
							<TextInput
								size="sm"
								value={chartConfig.yAxisConfig?.yAxisCustomTitle || ''}
								onChange={(event) => {
									onUpdate(
										updateYAxisTitle(chartConfig, event.currentTarget.value)
									);
								}}
								placeholder={chartConfigHelper.yAxisTitle}
							/>
						</Group>
					</SelectableProperty>
				)}
				<SelectableProperty
					label="Show axis label"
					dataTestId="y-show-axis-label"
				>
					<Group w="100%" position="right">
						<Switch
							checked={chartConfig.yAxisConfig?.yAxisShowTicks ?? true}
							onChange={(event) => {
								onUpdate(
									updateYAxisShowLabel(chartConfig, event.currentTarget.checked)
								);
							}}
						/>
					</Group>
				</SelectableProperty>
				<SelectableProperty
					label="Start axis at zero"
					dataTestId="y-start-at-zero"
				>
					<Group w="100%" position="right">
						<Switch
							checked={chartConfig.yAxisConfig?.yAxisStartAtZero ?? true}
							onChange={(event) => {
								onUpdate(
									updateYAxisStartAtZero(
										chartConfig,
										event.currentTarget.checked
									)
								);
							}}
						/>
					</Group>
				</SelectableProperty>
			</Stack>
		);
	}
);
